/**
 * Verdict splash — the door's full-viewport, colour-coded terminal
 * announcement: colour, motion, sound/haptics, and HOLD's own calm settle-in
 * (vs. the terminal states' decisive overshoot) render the current terminal
 * verdict `verdictState` carries.
 *
 * Reads `verdictState` directly rather than accepting a `.verdict` property
 * — the signal is the API: this element's whole reason for being
 * is rendering `verdictState`'s own current terminal verdict, so a property
 * would only duplicate what the shared signal already carries. Every
 * operator tap (verify/retry/dismiss/undo) calls straight into
 * verdict.state.ts's own exported Subjects — `verdictState` keeps its
 * single writer (verdict.sync.ts); this file never writes it.
 *
 * scan.view.ts keys this element by the resolved Verdict object. A new
 * terminal answer gets a fresh mount even when confirming and resolution
 * batch into one render. Disconnect cancels the previous answer's timers;
 * connectedCallback owns this answer's sound, haptics and hold duration.
 *
 * ALREADY IN stays interactive throughout its hold and carries the same
 * re-attempt affordance HELD has, never a passive pointer-events:none wait:
 * a currently-valid ticket rendering ALREADY IN off a briefly-stale index is
 * the exact signature of pass-back fraud, so the operator needs a live
 * retry path, not a hold with no control. `retryVerdict$` — the sibling
 * channel verdict.sync.ts's own "retry" intent — special-cases `already-in`
 * as a CAS re-attempt against the known ticket ref, never a direct state
 * write.
 *
 * HELD's "still waiting" cue rides on its own icon (`motion-safe:animate-pulse`
 * in render()), never a whole-:host opacity oscillation: an infinite host-level
 * dim also dims the headline, body sentence, and the retry/set-aside controls
 * riding on the same fill, undercutting the exact controls the operator needs
 * mid-wait. `prefers-reduced-motion` disables the icon pulse the same way it
 * disables every other motion here; the headline and body sentence already
 * say "still waiting" in words, so the state reads correctly either way.
 *
 * `canUndo` is a property, not a `checkin.state.ts` read: this component
 * mounts under three different callers (the scanner's own phone chrome, the
 * admin's chrome, the design studio's fixtures), each already holding
 * its own `hasUndoPermission` answer against its own session, so the caller
 * carries undo authority down as a prop instead of this file reaching into
 * the shared signal itself — mirrors `settings.view.ts`'s own
 * `canChangeEvent` delta.
 */
import { sound } from "@mhmo91/schmancy";
import { SchmancyElement, schmancyElement } from "@mhmo91/schmancy/mixins";
import { type Locale, type MessageKey, translate } from "@momo/i18n";
import { css, html } from "lit";
import { property, state } from "lit/decorators.js";
import { choose } from "lit/directives/choose.js";
import { ifDefined } from "lit/directives/if-defined.js";
import { when } from "lit/directives/when.js";
import { timer } from "rxjs";
import { takeUntil } from "rxjs/operators";
import { LocalizeMixin } from "../../i18n/localize.ts";
import { hapticsEnabled } from "../settings/settings.state.ts";
import { ticketTypeName } from "../ticket-types/ticket-types.state.ts";
import {
	dismissRequested$,
	holderName,
	retryVerdict$,
	unredeemTapped$,
	type Verdict,
	verdictState,
} from "./verdict.state.ts";

export type TerminalVerdict = Exclude<
	Verdict,
	{ kind: "confirming" | "pending-admit" }
>;

type Tone = TerminalVerdict["kind"];

const TONE_ICON: Record<Tone, string> = {
	admit: "check_circle",
	"already-in": "how_to_reg",
	stop: "block",
	held: "front_hand",
};

const TONE_HEADLINE_KEY: Record<Tone, MessageKey> = {
	admit: "scanner.verdict.headline.admit",
	"already-in": "scanner.verdict.headline.alreadyIn",
	stop: "scanner.verdict.headline.stop",
	held: "scanner.verdict.headline.held",
};

// The "on" color for each tone's own semantic fill — the radial gradient's
// center sits at that tone's full-saturation `-default`, so its matching
// `-on` token is the token-driven legible pairing (never raw white), same
// reasoning as any filled schmancy-button.
const TONE_ON_CLASS: Record<Tone, string> = {
	admit: "text-success-on",
	"already-in": "text-warning-on",
	stop: "text-error-on",
	held: "text-info-on",
};

// Auto-dismiss durations — decisive verdicts clear themselves; HOLD never
// does (it demands a retry or a deliberate set-aside). ALREADY IN holds
// longer than ADMIT on purpose: it is the state most likely to need a beat
// of operator judgment, not a fast pass-through — and, per the departure
// above, stays interactive for the whole window rather than a passive wait.
const AUTO_DISMISS_MS: Partial<Record<Tone, number>> = {
	admit: 950,
	"already-in": 1500,
	stop: 1700,
};

const SOUND_FOR: Record<Tone, Parameters<typeof sound.play>[0] | null> = {
	admit: "joyful",
	"already-in": "calm",
	stop: "frustrated",
	held: "worried",
};

// navigator.vibrate patterns mirror the sound signature 1:1 — the one
// feedback channel both source systems left unwired.
const HAPTIC_FOR: Record<Tone, readonly number[] | null> = {
	admit: [40, 60, 40],
	"already-in": [60],
	stop: [50, 80, 50, 80, 50],
	held: [30, 40, 30],
};

/** Fresh, human-legible precision — "4 seconds ago" — never a blurred
 *  humanize() that renders a 4-second-old stutter and a genuine second
 *  attempt identically. Exported — this file's own single call site earns
 *  the export, per mechanical-code's public-export carve-out. */
export function formatAgo(millis: number, locale: Locale): string {
	const seconds = Math.round((Date.now() - millis) / 1000);
	const rtf = new Intl.RelativeTimeFormat(locale, { numeric: "auto" });
	if (seconds < 60) return rtf.format(-seconds, "second");
	const minutes = Math.round(seconds / 60);
	if (minutes < 60) return rtf.format(-minutes, "minute");
	return rtf.format(-Math.round(minutes / 60), "hour");
}

@schmancyElement("verdict-splash-view")
export class VerdictSplashView extends LocalizeMixin(SchmancyElement) {
	static styles = [
		css`
			:host {
				/* fixed, not absolute: the studio proof nests this inside its own
				   position:relative phone-frame mockup; a real scanner
				   app IS the viewport, so this covers it directly. */
				position: fixed;
				inset: 0;
				display: flex;
				align-items: center;
				justify-content: center;
				z-index: 40;
				pointer-events: none;
				animation: verdict-decisive 420ms cubic-bezier(0.2, 0.8, 0.2, 1) both;
			}
			:host([tone="admit"]) {
				background: radial-gradient(circle, var(--schmancy-sys-color-success-default) 0%, transparent 68%);
			}
			:host([tone="already-in"]) {
				/* the one deliberate departure this file's own header explains:
				   interactive throughout its hold, never a passive wait. */
				pointer-events: auto;
			}
			:host([tone="already-in"][data-self="false"]) {
				/* Full saturation held to 32% before fading — "stop"'s own
				   technique — instead of fading from 0%: more visual alarm
				   without moving off the warning hue. */
				background: radial-gradient(
					circle,
					var(--schmancy-sys-color-warning-default) 0%,
					var(--schmancy-sys-color-warning-default) 32%,
					transparent 68%
				);
			}
			:host([tone="already-in"][data-self="true"]) {
				/* No blanket host opacity: the redeemed-at timestamp below is
				   the operationally critical fact here too, and washing the
				   whole splash hides it along with everything else. Self reads
				   only slightly softer than stranger — a shorter saturation
				   hold, not a dimmed page. */
				background: radial-gradient(
					circle,
					var(--schmancy-sys-color-warning-default) 0%,
					var(--schmancy-sys-color-warning-default) 18%,
					transparent 55%
				);
			}
			:host([tone="stop"]) {
				/* The center content sits on true error-default, not a
				   fast-fading blend toward black — a gradient that starts
				   fading at 0% muddies to a desaturated pink before it
				   reaches the centered text, undercutting TONE_ON_CLASS's
				   text-error-on pairing (designed for text on a solid
				   error-default fill). Holding full saturation to 35% keeps
				   the centered content on the real color. */
				background: radial-gradient(
					circle,
					var(--schmancy-sys-color-error-default) 0%,
					var(--schmancy-sys-color-error-default) 35%,
					transparent 68%
				);
			}
			:host([tone="held"]) {
				pointer-events: auto;
				background: radial-gradient(circle, var(--schmancy-sys-color-info-default) 0%, transparent 62%);
				/* Overrides the base :host's bouncy scale-overshoot entrance: HOLD
				   settles in rather than resolving with a decisive pop. */
				animation: verdict-settle 320ms ease-out both;
			}
			@keyframes verdict-decisive {
				0% {
					transform: scale(0.85);
					opacity: 0;
				}
				55% {
					transform: scale(1.08);
				}
				100% {
					transform: scale(1);
					opacity: 1;
				}
			}
			@keyframes verdict-settle {
				from {
					opacity: 0;
				}
				to {
					opacity: 1;
				}
			}
			@media (prefers-reduced-motion: reduce) {
				:host {
					animation: none;
				}
				:host([tone="held"]) {
					animation: none;
				}
			}
		`,
	];

	@property({ type: Boolean }) canUndo = false;

	@state() private verdict: TerminalVerdict | null = null;
	@state() private showParty = false;

	override connectedCallback() {
		super.connectedCallback();
		const v = verdictState.value.verdict;
		if (v === null || v.kind === "confirming" || v.kind === "pending-admit") {
			// scan.view.ts only mounts this element for a terminal verdict —
			// reached only if that gate breaks.
			return;
		}
		this.verdict = v;
		this.setAttribute("tone", v.kind);
		if (v.kind === "already-in") this.setAttribute("data-self", String(v.self));

		// sound.play() self-gates on schmancy's own mute setting
		// (sound.service.ts's own `play()`) — no separate on/off check here.
		const feeling = SOUND_FOR[v.kind];
		if (feeling) sound.play(feeling);
		if (hapticsEnabled.value && navigator.vibrate) {
			const pattern = HAPTIC_FOR[v.kind];
			if (pattern) navigator.vibrate([...pattern]);
		}

		if (v.kind === "admit" && v.party) {
			timer(400)
				.pipe(takeUntil(this.disconnecting))
				.subscribe(() => {
					this.showParty = true;
				});
		}

		const holdMs = AUTO_DISMISS_MS[v.kind];
		if (holdMs !== undefined) {
			timer(holdMs)
				.pipe(takeUntil(this.disconnecting))
				.subscribe(() => dismissRequested$.next());
		}
	}

	override render() {
		const v = this.verdict;
		if (v === null) return html``;
		const tone = TONE_ON_CLASS[v.kind];
		// Directly under the headline, above the kind-specific subline: whose
		// ticket this is outranks which type it is once both apply. Null for
		// every kind that resolves no ticket, and null until the holder read
		// settles — the splash renders its verdict without it either way.
		const holder = holderName.get();
		return html`
			<div class="pointer-events-none flex flex-col items-center gap-3 px-8 text-center">
				<schmancy-icon
					class=${this.classMap({
						"text-6xl": true,
						[tone]: true,
						"motion-safe:animate-pulse": v.kind === "held",
					})}
					>${TONE_ICON[v.kind]}</schmancy-icon
				>
				<schmancy-typography
					type="display"
					weight=${ifDefined(v.kind === "already-in" ? "bold" : undefined)}
					class=${this.classMap({ [tone]: true })}
				>
					${translate(this.uiLanguage, TONE_HEADLINE_KEY[v.kind])}
				</schmancy-typography>
				${when(
					holder !== null,
					() => html`
						<div class="flex items-center gap-2">
							<schmancy-icon class=${this.classMap({ [tone]: true })}>redeem</schmancy-icon>
							<schmancy-typography type="title" token="md" class=${this.classMap({ [tone]: true })}>
								${translate(this.uiLanguage, "scanner.verdict.holder", { name: holder ?? "" })}
							</schmancy-typography>
						</div>
					`,
				)}
				${choose(v.kind, [
					[
						"admit",
						() => {
							if (v.kind !== "admit") return html``;
							const typeName =
								ticketTypeName(v.ticket.typeId, this.uiLanguage) ?? "";
							const party = v.party;
							const partyText = party
								? translate(
										this.uiLanguage,
										"scanner.verdict.admit.partyRemaining",
										{
											count: party.othersRemaining,
										},
									) +
									(party.othersAlreadyIn > 0
										? translate(
												this.uiLanguage,
												"scanner.verdict.admit.partyAlreadyInSuffix",
												{
													count: party.othersAlreadyIn,
												},
											)
										: "")
								: null;
							return html`
								<schmancy-typography type="headline" token="sm" class=${this.classMap({ [tone]: true })}>
									${typeName}
								</schmancy-typography>
								${when(
									this.showParty && partyText !== null,
									() => html`
										<schmancy-typography type="label" token="md" class=${this.classMap({ "mt-2 flex items-center gap-2": true, [tone]: true })}>
											<schmancy-icon>groups</schmancy-icon>
											${partyText}
										</schmancy-typography>
									`,
								)}
							`;
						},
					],
					[
						"already-in",
						() => {
							if (v.kind !== "already-in") return html``;
							const ago =
								v.redeemedAtMillis === null
									? null
									: formatAgo(v.redeemedAtMillis, this.uiLanguage);
							// One combined line, not the old headline-echoing sentence
							// plus a footnote-sized timestamp: WHEN is the fact an
							// operator needs at a glance (stale duplicate vs. a live
							// conflict), so it leads, sized and weighted to match — and
							// WHICH DOOR (this one, for self, or the other one) rides
							// beside it rather than repeating "already admitted" a
							// second time under a headline that already says so.
							const where = v.self
								? translate(this.uiLanguage, "scanner.verdict.alreadyIn.self")
								: (v.doorLabel ??
									translate(
										this.uiLanguage,
										"scanner.verdict.alreadyIn.unknownDoor",
									));
							return html`
								<div class="mt-1 flex items-center gap-2">
									<schmancy-icon class=${this.classMap({ [tone]: true })}>history</schmancy-icon>
									<schmancy-typography type="headline" token="sm" weight="bold" class=${this.classMap({ [tone]: true })}>
										${ago !== null ? `${ago} · ${where}` : where}
									</schmancy-typography>
								</div>
								<div class="pointer-events-auto mt-3 flex gap-2">
									<!-- filled/neutral, not outlined: an outlined button's border+text
									     assume a page background, and directly on the tone's own full-
									     saturation fill that pairing loses almost all contrast (the same
									     failure mode this proof's own header names for tone="stop"). An
									     opaque neutral surface —
									     stays legible regardless of what the gradient underneath is doing. -->
									<schmancy-button variant="filled" color="neutral" @click=${() => retryVerdict$.next()}>
										<schmancy-icon slot="prefix">restart_alt</schmancy-icon>
										${translate(this.uiLanguage, "scanner.verdict.alreadyIn.verify")}
									</schmancy-button>
									${when(
										this.canUndo,
										() => html`
											<schmancy-button variant="filled tonal" color="neutral" @click=${() => unredeemTapped$.next()}>
												${translate(this.uiLanguage, "scanner.verdict.undo.action")}
											</schmancy-button>
										`,
									)}
								</div>
							`;
						},
					],
					[
						"stop",
						() =>
							v.kind !== "stop"
								? html``
								: html`<schmancy-typography type="headline" token="sm" class=${this.classMap({ [tone]: true })}
										>${v.detail}</schmancy-typography
									>`,
					],
					[
						"held",
						() => {
							if (v.kind !== "held") return html``;
							return html`
								<schmancy-typography type="headline" token="sm" class=${this.classMap({ [tone]: true })}>
									${translate(
										this.uiLanguage,
										v.cause === "offline"
											? "scanner.verdict.held.offline"
											: "scanner.verdict.held.timeout",
									)}
								</schmancy-typography>
								<schmancy-typography type="body" token="md" class=${this.classMap({ "max-w-xs": true, [tone]: true })}>
									${translate(this.uiLanguage, "scanner.verdict.held.body")}
								</schmancy-typography>
								<div class="pointer-events-auto mt-2 flex gap-2">
									<schmancy-button variant="filled" color="neutral" @click=${() => retryVerdict$.next()}>
										<schmancy-icon slot="prefix">restart_alt</schmancy-icon>
										${translate(this.uiLanguage, "scanner.verdict.held.retry")}
									</schmancy-button>
									<!-- filled tonal/neutral, not outlined: an outlined chip's
									     border+text assume a page background, and directly on
									     the tone's own saturated fill that pairing nearly
									     disappears — same failure already-in's own buttons had. -->
									<schmancy-button variant="filled tonal" color="neutral" @click=${() => dismissRequested$.next()}>
										${translate(this.uiLanguage, "scanner.verdict.held.setAside")}
									</schmancy-button>
								</div>
							`;
						},
					],
				])}
			</div>
		`;
	}
}

declare global {
	interface HTMLElementTagNameMap {
		"verdict-splash-view": VerdictSplashView;
	}
}
